/**
 * Cloud storage client for TileForge assets via R2.
 * Falls back to IndexedDB for anonymous/offline use.
 *
 * Usage:
 *   const storage = new CloudStorage();
 *   await storage.init();
 *   await storage.uploadTileset('grass.png', blob);
 *   const url = storage.getAssetURL('grass.png');
 */

const API_BASE = 'https://tileforge-api.echoforge.games';
const IDB_NAME = 'tileforge-assets';
const IDB_VERSION = 1;
const IDB_STORE = 'files';

export class CloudStorage {
  constructor() {
    this._auth = null;  // EchoForgeAuth reference — single source of truth for tokens
    this._db = null;
    this._unsubAuth = null;
    this._connectionMonitor = null;
  }

  async init(auth) {
    // Open IndexedDB (always available as local cache)
    this._db = await this._openIDB();

    // If an EchoForgeAuth instance is provided, bind to it
    if (auth) {
      this.bindAuth(auth);
    }
  }

  /** Bind to an EchoForgeAuth instance. Token is always read live from auth. */
  bindAuth(auth) {
    this._auth = auth;
    // Listen for auth changes (login, logout, token refresh)
    if (this._unsubAuth) this._unsubAuth();
    this._unsubAuth = auth.onAuthChange(() => {
      // No-op — we read from auth on demand, so no stale copy to update
    });
  }

  /** Bind a ConnectionMonitor for active health checking + offline queue. */
  bindConnectionMonitor(monitor) {
    this._connectionMonitor = monitor;
    // Process queued operations when connectivity returns
    monitor.onChange((state) => {
      if (state.online && state.pendingCount > 0 && !state.syncing) {
        monitor.processQueue((path, data, contentType) =>
          this._directUpload(path, data, contentType)
        );
      }
    });
  }

  get isOnline() {
    // Use ConnectionMonitor if available (active health checking)
    if (this._connectionMonitor) {
      return this._connectionMonitor.isOnline
        && this._auth?.isAuthenticated()
        && this._auth?.getUser()?.userId;
    }
    // Fallback: browser API + auth check
    if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
    return this._auth?.isAuthenticated() && this._auth?.getUser()?.userId;
  }

  get userId() {
    return this._auth?.getUser()?.userId || null;
  }

  /** @deprecated Use bindAuth() instead. Kept for backward compatibility. */
  signIn(userId, token) {
    // No-op when auth is bound — token comes from EchoForgeAuth
    if (this._auth) return;
  }

  /** @deprecated Use auth.signOut() instead. Kept for backward compatibility. */
  signOut() {
    // No-op when auth is bound — sign out through EchoForgeAuth
    if (this._auth) return;
  }

  /** Get the current token synchronously (may be null if expired). */
  _getToken() {
    if (!this._auth) return null;
    return this._auth.getToken();
  }

  /** Get a valid token, waiting for refresh if needed. */
  async _getTokenAsync() {
    if (!this._auth) return null;
    return this._auth.getTokenAsync();
  }

  /**
   * Validate a file before upload. Returns null if OK, error string if not.
   */
  validateFile(file, contentType) {
    const limits = {
      'image/png': 512 * 1024,
      'image/webp': 512 * 1024,
      'application/json': 1024 * 1024,
    };

    const allowed = Object.keys(limits);
    if (!allowed.includes(contentType)) {
      return `File type "${contentType}" not supported. Use PNG, WebP, or JSON.`;
    }

    const limit = limits[contentType];
    const size = file.size || file.byteLength || 0;
    if (size > limit) {
      const limitKB = Math.round(limit / 1024);
      const gotKB = Math.round(size / 1024);
      return `File is ${gotKB} KB — max is ${limitKB} KB for ${contentType.startsWith('image') ? 'images' : 'map files'}. Resize your tileset or re-export at a smaller size.`;
    }

    return null;
  }

  /**
   * Upload a file. Stores in R2 if online, IndexedDB always.
   * @param {string} path - File path (e.g., 'tilesets/grass.png')
   * @param {Blob|ArrayBuffer} data - File content
   * @param {string} contentType - MIME type
   */
  async upload(path, data, contentType = 'application/octet-stream') {
    // Always cache locally
    await this._idbPut(path, data, contentType);

    // Upload to R2 if online
    if (this.isOnline) {
      const result = await this._directUpload(path, data, contentType);
      if (result) return { stored: 'cloud', ...result };
    }

    // Offline or upload failed — queue for later sync if we have a monitor
    if (this._connectionMonitor && this._auth?.isAuthenticated()) {
      const buf = data instanceof ArrayBuffer ? data
        : data instanceof Blob ? await data.arrayBuffer()
        : null;
      if (buf) {
        await this._connectionMonitor.queueOperation('upload', { path, data: buf, contentType });
        return { stored: 'queued', path };
      }
    }

    return { stored: 'local', path };
  }

  /**
   * Direct R2 upload (no queue, no fallback). Used by upload() and queue processor.
   * @returns {Object|null} — result from API, or null on failure.
   */
  async _directUpload(path, data, contentType) {
    try {
      if (!this.userId) return null; // Signed out — can't upload
      const token = await this._getTokenAsync();
      if (!token) return null;

      const response = await fetch(`${API_BASE}/assets/${this.userId}/${path}`, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
          'Authorization': `Bearer ${token}`,
        },
        body: data,
      });

      if (!response.ok) {
        console.warn(`R2 upload failed for ${path}:`, await response.text());
        return null;
      }

      return await response.json();
    } catch (err) {
      console.warn('R2 upload failed:', err.message);
      return null;
    }
  }

  /**
   * Download a file. Tries IndexedDB first (fast, local), then R2 if not found.
   * @param {string} path - File path
   * @returns {Promise<{data: Blob, contentType: string} | null>}
   */
  async download(path) {
    // Try local first — faster and avoids 404 noise for locally-provisioned files
    const local = await this._idbGet(path);
    if (local) return local;

    // Try R2 if online
    if (this.isOnline) {
      try {
        const response = await fetch(`${API_BASE}/assets/${this.userId}/${path}`);
        if (response.ok) {
          const data = await response.blob();
          const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
          // Cache locally
          await this._idbPut(path, data, contentType);
          return { data, contentType };
        }
      } catch (err) {
        // Network error — already checked local
      }
    }

    return null;
  }

  /**
   * Get a URL for an asset. Returns R2 URL if online, object URL from IDB if not.
   * @param {string} path - File path
   * @returns {string|null}
   */
  getAssetURL(path) {
    if (this.isOnline) {
      return `${API_BASE}/assets/${this.userId}/${path}`;
    }
    return null; // Caller should use download() for local assets
  }

  /**
   * Load an asset as an HTMLImageElement.
   * Tries IndexedDB first, then R2 if not found locally.
   * @param {string} path - File path
   * @returns {Promise<HTMLImageElement>}
   */
  async loadImage(path) {
    // Try local first — faster and avoids 404 noise
    const local = await this._loadImageFromIDB(path);
    if (local) return local;

    // Try R2 if online
    if (this.isOnline) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Image not found: ${path}`));
        img.src = `${API_BASE}/assets/${this.userId}/${path}`;
      });
    }

    throw new Error(`Image not found locally: ${path}`);
  }

  /** Delete a file from both R2 and IndexedDB. */
  async delete(path) {
    await this._idbDelete(path);

    if (this.isOnline) {
      try {
        const token = await this._getTokenAsync();
        if (token) {
          await fetch(`${API_BASE}/assets/${this.userId}/${path}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
        }
      } catch (err) {
        console.warn('R2 delete failed:', err.message);
      }
    }
  }

  /** List all files. Merges R2 + local. */
  async list() {
    const localFiles = await this._idbList();

    if (this.isOnline) {
      try {
        const token = await this._getTokenAsync();
        const response = await fetch(`${API_BASE}/assets/${this.userId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (response.ok) {
          const result = await response.json();
          return {
            cloud: result.files,
            local: localFiles,
            totalCloudSize: result.totalSize,
            quota: result.quotaLimit,
          };
        }
      } catch (err) {
        // Fall through
      }
    }

    return { cloud: [], local: localFiles, totalCloudSize: 0, quota: 'local only' };
  }

  /**
   * List files matching a prefix. Returns array of file info objects.
   * @param {string} prefix - Path prefix to filter by (e.g., 'tilesets/')
   * @returns {Promise<Array<{path: string, contentType: string, size: number}>>}
   */
  async listByPrefix(prefix) {
    const listing = await this.list();
    const allFiles = [...(listing.local || []), ...(listing.cloud || [])];
    const seen = new Set();
    const results = [];
    for (const f of allFiles) {
      const path = f.path || f.key || f;
      if (typeof path === 'string' && path.startsWith(prefix) && !seen.has(path)) {
        seen.add(path);
        results.push({ path, contentType: f.contentType, size: f.size || 0 });
      }
    }
    return results;
  }

  /** Sync local files to cloud. */
  async syncToCloud() {
    if (!this.isOnline) return { synced: 0 };

    const localFiles = await this._idbList();
    let synced = 0;

    for (const file of localFiles) {
      const local = await this._idbGet(file.path);
      if (local) {
        await this.upload(file.path, local.data, local.contentType);
        synced++;
      }
    }

    return { synced };
  }

  // --- IndexedDB helpers ---

  _openIDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(IDB_NAME, IDB_VERSION);
      request.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE, { keyPath: 'path' });
        }
      };
      request.onsuccess = (e) => resolve(e.target.result);
      request.onerror = (e) => reject(e.target.error);
    });
  }

  _idbPut(path, data, contentType) {
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put({
        path,
        data: data instanceof Blob ? data : new Blob([data]),
        contentType,
        updatedAt: Date.now(),
      });
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e.target.error);
    });
  }

  _idbGet(path) {
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction(IDB_STORE, 'readonly');
      const request = tx.objectStore(IDB_STORE).get(path);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = (e) => reject(e.target.error);
    });
  }

  _idbDelete(path) {
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(path);
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e.target.error);
    });
  }

  _idbList() {
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction(IDB_STORE, 'readonly');
      const request = tx.objectStore(IDB_STORE).getAll();
      request.onsuccess = () => {
        resolve((request.result || []).map(r => ({
          path: r.path,
          contentType: r.contentType,
          size: r.data?.size || 0,
          updatedAt: r.updatedAt,
        })));
      };
      request.onerror = (e) => reject(e.target.error);
    });
  }

  async _loadImageFromIDB(path) {
    const entry = await this._idbGet(path);
    if (!entry) return null;
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(entry.data);
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed')); };
      img.src = url;
    });
  }
}
